/**
 * Iframe-side client for the host-bridge postMessage protocol.
 *
 * The games-web app instantiates a single {@link IframeClient} at boot. It:
 *
 *  - Validates inbound messages against the origin allow-list and against
 *    `window.parent` as the source (no neighbor iframes, no opener tricks).
 *  - Lets callers subscribe to host lifecycle messages (`init`, `pause`, ...).
 *  - Exposes typed emitters for the fire-and-forget iframe → host messages
 *    (`session_started`, `queue_event`, `analytics_event`, ...).
 *  - Implements request/response correlation for the bridge calls
 *    (`request_haptic`, `request_share`, `request_close`): each outbound
 *    request is registered in a `Map<id, pending>`, and the matching
 *    inbound `bridge_response` (matched on `replyTo === id`) settles the
 *    promise. Outstanding requests time out after `requestTimeoutMs` and
 *    are rejected on `dispose()`.
 *
 * Origin/source validation is intentionally non-negotiable: callers must
 * supply at least one allowed origin. Construct with `['*']` only as a
 * conscious dev-only escape hatch.
 */
import { hostBridgeResponse, iframeAchievementUnlocked, iframeAnalyticsEvent, iframeError, iframeFocusOut, iframeQueueEvent, iframeReady, iframeRequestClose, iframeRequestHaptic, iframeRequestShare, iframeSessionEnded, iframeSessionStarted, parseHostMessage, } from '@rendever/games-contracts';
import { BridgeDisposedError, BridgeRejectedError, BridgeTimeoutError } from './errors.js';
import { isAllowedOrigin } from './origin.js';
const DEFAULT_REQUEST_TIMEOUT_MS = 8_000;
// ---------------------------------------------------------------------------
// IframeClient
// ---------------------------------------------------------------------------
export class IframeClient {
    allowedOrigins;
    targetOrigin;
    requestTimeoutMs;
    win;
    parentWin;
    handlers = new Map();
    pending = new Map();
    disposed = false;
    listener;
    constructor(opts) {
        if (!opts.allowedOrigins || opts.allowedOrigins.length === 0) {
            throw new Error('IframeClient: `allowedOrigins` must be a non-empty array');
        }
        this.allowedOrigins = opts.allowedOrigins;
        this.requestTimeoutMs = opts.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
        if (opts.targetOrigin === undefined) {
            // Last-resort fallback. We post to '*' so dev/local-file iframes still
            // function, but warn loudly because production hosts MUST supply this.
            console.warn('[host-bridge:iframe] No `targetOrigin` supplied; falling back to "*". ' +
                'Hosts should provide their explicit origin.');
            this.targetOrigin = '*';
        }
        else {
            this.targetOrigin = opts.targetOrigin;
        }
        const win = opts.win ?? (typeof window !== 'undefined' ? window : undefined);
        if (!win) {
            throw new Error('IframeClient: no `window` available — pass `win` explicitly');
        }
        this.win = win;
        this.parentWin = opts.parentWin ?? win.parent;
        this.listener = (event) => this.handleMessage(event);
        this.win.addEventListener('message', this.listener);
    }
    // -------------------------------------------------------------------------
    // Public API — lifecycle subscription
    // -------------------------------------------------------------------------
    /**
     * Subscribe to a host-initiated message. Returns an unsubscribe function.
     * Subscribers do not see invalid (bad origin / bad source / malformed)
     * messages — those are filtered before dispatch.
     */
    on(type, handler) {
        let set = this.handlers.get(type);
        if (!set) {
            set = new Set();
            this.handlers.set(type, set);
        }
        // Stored as a wider Handler so the Map is homogeneous; the cast back at
        // dispatch time is safe because we keyed by `type`.
        set.add(handler);
        return () => {
            const current = this.handlers.get(type);
            if (current)
                current.delete(handler);
        };
    }
    // -------------------------------------------------------------------------
    // Public API — ready signal
    // -------------------------------------------------------------------------
    ready(payload) {
        this.assertNotDisposed();
        const msg = iframeReady({
            appVersion: payload.appVersion,
            supportedFeatures: payload.supportedFeatures ?? [],
        });
        this.post(msg);
    }
    // -------------------------------------------------------------------------
    // Public API — bridge requests (request/response correlation)
    // -------------------------------------------------------------------------
    requestHaptic(style) {
        return this.request(iframeRequestHaptic({ style }));
    }
    requestShare(payload) {
        // Strip undefined-valued optionals to satisfy exactOptionalPropertyTypes
        // when forwarding to the constructor.
        const built = { text: payload.text };
        if (payload.title !== undefined)
            built.title = payload.title;
        if (payload.url !== undefined)
            built.url = payload.url;
        return this.request(iframeRequestShare(built));
    }
    requestClose() {
        return this.request(iframeRequestClose());
    }
    /**
     * Send an outbound request and wait for a matching `bridge_response`.
     * Resolves with the host's `payload` (cast to T) or `undefined` if the
     * host omitted it. Rejects on `ok: false`, on timeout, or on dispose.
     */
    request(msg) {
        this.assertNotDisposed();
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                if (this.pending.delete(msg.id)) {
                    reject(new BridgeTimeoutError(`Bridge request "${msg.type}" timed out`));
                }
            }, this.requestTimeoutMs);
            const entry = {
                resolve,
                reject,
                timer,
            };
            this.pending.set(msg.id, entry);
            this.post(msg);
        });
    }
    // -------------------------------------------------------------------------
    // Public API — fire-and-forget emitters
    // -------------------------------------------------------------------------
    sendSessionStarted(payload) {
        this.post(iframeSessionStarted(payload));
    }
    sendSessionEnded(payload) {
        this.post(iframeSessionEnded(payload));
    }
    sendAchievementUnlocked(payload) {
        this.post(iframeAchievementUnlocked(payload));
    }
    /**
     * Forward a durable event to the host's IndexedDB outbox.
     *
     * The bridge does not persist anything itself — that is the host's job.
     * If the iframe is disposed mid-send the message is simply dropped; the
     * SDK is expected to retry from its own buffer.
     */
    sendQueueEvent(payload) {
        this.post(iframeQueueEvent({
            sessionPublicId: payload.sessionPublicId,
            seq: payload.seq,
            occurredAt: payload.occurredAt,
            elapsedMs: payload.elapsedMs,
            eventType: payload.eventType,
            reactionTimeMs: payload.reactionTimeMs ?? null,
            payload: payload.payload ?? {},
        }));
    }
    sendFocusOut(direction) {
        this.post(iframeFocusOut({ direction }));
    }
    sendAnalyticsEvent(name, props = {}) {
        this.post(iframeAnalyticsEvent({ name, props }));
    }
    sendError(payload) {
        const built = {
            code: payload.code,
            message: payload.message,
            severity: payload.severity ?? 'fatal',
            context: payload.context ?? {},
        };
        if (payload.sessionPublicId !== undefined)
            built.sessionPublicId = payload.sessionPublicId;
        this.post(iframeError(built));
    }
    // -------------------------------------------------------------------------
    // Public API — SDK Transport adapter
    // -------------------------------------------------------------------------
    /**
     * Return an object satisfying the `@rendever/games-sdk` Transport
     * interface. The SDK emits an outbound event with a `kind` discriminant;
     * this adapter switches on `kind` and forwards to the matching `sendXxx`.
     *
     * Reconciliation note: the SDK's `Transport.send` is async, but
     * `postMessage` is synchronous. The adapter resolves immediately after
     * posting. If the bridge is disposed, `send` rejects so the SDK can buffer
     * and retry. Bridge-request kinds (haptic/share/close) are NOT in the SDK
     * Transport union — those are explicitly exposed on the client itself
     * because they have a return value.
     */
    /**
     * Implementation of the SDK Transport interface, exposed as a method on
     * the client itself for convenience: `iframeClient` satisfies
     * `Transport` directly, no `.asTransport()` call required. The
     * `asTransport()` method exists as well for cases where the call site
     * wants a narrow object that captures `this`.
     */
    async send(event) {
        if (this.disposed) {
            throw new BridgeDisposedError('IframeClient.send: cannot send after dispose()');
        }
        switch (event.kind) {
            case 'session_started':
                this.sendSessionStarted(event.payload);
                return;
            case 'session_ended':
                this.sendSessionEnded(event.payload);
                return;
            case 'achievement_unlocked':
                this.sendAchievementUnlocked(event.payload);
                return;
            case 'queue_event':
                this.sendQueueEvent(event.payload);
                return;
            case 'analytics_event': {
                const p = event.payload;
                this.sendAnalyticsEvent(p.name, p.props);
                return;
            }
            default: {
                const unhandled = event.kind;
                throw new Error(`IframeClient.send: unhandled event kind ${String(unhandled)}`);
            }
        }
    }
    asTransport() {
        return {
            send: async (event) => {
                if (this.disposed) {
                    throw new BridgeDisposedError('IframeClient.asTransport: cannot send after dispose()');
                }
                switch (event.kind) {
                    case 'session_started':
                        this.sendSessionStarted(event.payload);
                        return;
                    case 'session_ended':
                        this.sendSessionEnded(event.payload);
                        return;
                    case 'achievement_unlocked':
                        this.sendAchievementUnlocked(event.payload);
                        return;
                    case 'queue_event':
                        this.sendQueueEvent(event.payload);
                        return;
                    case 'analytics_event': {
                        const p = event.payload;
                        this.sendAnalyticsEvent(p.name, p.props);
                        return;
                    }
                    default: {
                        // Exhaustiveness check — adding a new TransportOutbound kind in
                        // the SDK without updating this switch will land here.
                        const unhandled = event.kind;
                        throw new Error(`IframeClient.asTransport: unhandled event kind ${String(unhandled)}`);
                    }
                }
            },
        };
    }
    // -------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------
    dispose() {
        if (this.disposed)
            return;
        this.disposed = true;
        this.win.removeEventListener('message', this.listener);
        this.handlers.clear();
        for (const [, entry] of this.pending) {
            clearTimeout(entry.timer);
            entry.reject(new BridgeDisposedError());
        }
        this.pending.clear();
    }
    // -------------------------------------------------------------------------
    // Internals
    // -------------------------------------------------------------------------
    assertNotDisposed() {
        if (this.disposed) {
            throw new BridgeDisposedError('IframeClient: operation called after dispose()');
        }
    }
    post(message) {
        if (this.disposed)
            return;
        // `postMessage` accepts the structured-clone-serializable message.
        this.parentWin.postMessage(message, this.targetOrigin);
    }
    handleMessage(event) {
        if (this.disposed)
            return;
        if (!isAllowedOrigin(event.origin, this.allowedOrigins))
            return;
        if (event.source !== this.parentWin)
            return;
        const parsed = parseHostMessage(event.data);
        if (!parsed.ok)
            return;
        const msg = parsed.data;
        if (msg.type === 'bridge_response') {
            this.handleBridgeResponse(msg);
            return;
        }
        this.dispatch(msg);
    }
    handleBridgeResponse(msg) {
        const entry = this.pending.get(msg.replyTo);
        if (!entry)
            return; // unknown / expired correlation id — ignore
        this.pending.delete(msg.replyTo);
        clearTimeout(entry.timer);
        if (msg.ok) {
            // `payload` is optional on success; resolve with `undefined` or the host's data.
            entry.resolve(msg.payload);
        }
        else {
            entry.reject(new BridgeRejectedError(msg.error ?? 'Bridge request rejected by host'));
        }
    }
    dispatch(msg) {
        if (msg.type === 'bridge_response')
            return; // already handled
        const set = this.handlers.get(msg.type);
        if (!set || set.size === 0)
            return;
        // Copy to a static array so handlers that unsubscribe mid-dispatch don't
        // mutate the iteration.
        for (const handler of [...set]) {
            try {
                // Safe cast: dispatched only to handlers registered under msg.type.
                handler(msg);
            }
            catch (err) {
                console.error('[host-bridge:iframe] handler threw', err);
            }
        }
    }
}
// Re-export the hostBridgeResponse constructor here too — handy for tests
// that stand up a fake host inside the iframe-side suite.
export { hostBridgeResponse };
//# sourceMappingURL=iframe.js.map